import { z } from 'zod';

import { ProcurementThreeWayReceiptGate } from './generated/enums.js';

export { ProcurementThreeWayReceiptGate };

export const ProcurementMatchingMode = {
  TWO_WAY: 'TWO_WAY',
  THREE_WAY: 'THREE_WAY',
} as const;
export type ProcurementMatchingMode =
  (typeof ProcurementMatchingMode)[keyof typeof ProcurementMatchingMode];

/**
 * Fallback applied when requireCompetitiveQuotes=true but minQuotes is null —
 * either a legacy settings row persisted before minQuotes became required, or any
 * other state where the minimum was never set. One vendor quote is the minimum
 * needed to satisfy "competitive quotes" at all, so 1 is the sane default.
 */
export const LEGACY_DEFAULT_MINIMUM_COMPETITIVE_QUOTES = 1;

export const PROCUREMENT_THREE_WAY_RECEIPT_GATE_VALUES = [
  ProcurementThreeWayReceiptGate.BEFORE_BILL,
  ProcurementThreeWayReceiptGate.BEFORE_PO_CLOSE,
] as const;

export const ProcurementGlobalSettingsSchema = z
  .object({
    allowPurchaseOrdersWithoutPurchaseRequest: z.boolean().default(false),
    enforcePurchaseRequestSelfApprovalGuard: z.boolean().default(true),
    requireVendor: z.boolean(),
    defaultPoExpirationDays: z.number().int().positive().nullable(),
    defaultAutoApproveUpTo: z.number().nonnegative().nullable(),
    autoCreateDraftBillOnReady: z.boolean(),
    matchingMode: z.enum(['TWO_WAY', 'THREE_WAY']).default('TWO_WAY'),
    allowedThreeWayReceiptGates: z
      .array(z.enum(['BEFORE_BILL', 'BEFORE_PO_CLOSE']))
      .default(['BEFORE_BILL', 'BEFORE_PO_CLOSE']),
    defaultThreeWayReceiptGate: z.enum(['BEFORE_BILL', 'BEFORE_PO_CLOSE']).default('BEFORE_BILL'),
    requireCompetitiveQuotes: z.boolean().default(false),
    minQuotes: z.number().int().positive().nullable().default(null),
    defaultOverBillingTolerancePercent: z.number().nonnegative().nullable().default(null),
    defaultOverBillingToleranceAmount: z.number().nonnegative().nullable().default(null),
    defaultBuyerUserIds: z.array(z.string().uuid()).default([]),
    defaultReceiverUserIds: z.array(z.string().uuid()).default([]),
    budgetExceptionApproverUserIds: z.array(z.string().uuid()).default([]),
  })
  .refine((settings) => !settings.requireCompetitiveQuotes || settings.minQuotes !== null, {
    message: 'minQuotes is required (at least 1) when requireCompetitiveQuotes is true',
    path: ['minQuotes'],
  });

export type ProcurementGlobalSettings = z.infer<typeof ProcurementGlobalSettingsSchema>;

export interface RecordPurchaseRequestVendorQuoteInput {
  vendorId: string;
  quotedAmount: number;
  quoteValidUntil?: string | Date | null;
  note?: string | null;
}

export interface SelectPurchaseRequestVendorInput {
  vendorId: string;
}

export interface RecordPurchaseOrderVendorQuoteInput {
  vendorId: string;
  quotedAmount: number;
  quoteValidUntil?: string | Date | null;
  note?: string | null;
}

export interface SelectPurchaseOrderVendorInput {
  vendorId: string;
}

export interface ReceivePurchaseOrderInput {
  receivedByUserId: string;
  note?: string | null;
  lines: {
    lineItemId: string;
    receivedQuantity: number;
  }[];
}

export interface LinkBillToPurchaseOrderInput {
  billId: string;
}
